import { gql } from '@apollo/client';

// ============================================
// FRAGMENTS
// ============================================

export const SURVEY_FRAGMENT = gql`
  fragment SurveyFields on Survey {
    id
    title
    slug
    description
    status
    pubblico
    startDate
    endDate
    totalQuestions
    totalSteps
    estimatedRecipients
    stats {
      totalResponses
      completedResponses
      inProgressResponses
      abandonedResponses
      averageCompletionTime
      completionRate
      targetRecipients
    }
    emailProgress {
      total
      sent
      failed
      status
    }
    createdBy {
      id
      username
    }
    createdAt
    updatedAt
  }
`;

export const SURVEY_FULL_FRAGMENT = gql`
  fragment SurveyFullFields on Survey {
    id
    title
    slug
    description
    status
    identitaVerificata
    pubblico
    startDate
    endDate
    settings {
      allowAnonymous
      allowMultipleResponses
      showProgressBar
      randomizeQuestions
      requireAllQuestions
      thankYouMessage
      redirectUrl
      allowSaveDraft
      timeLimitMinutes
      maxResponses
      notifyOnComplete
      notifyEmail
      emailNotifyOnPublish
      emailNotifyOnStart
      welcomeMessage
      closedMessage
    }
    steps {
      stepId
      order
      title
      description
      media {
        type
        url
        caption
      }
      questions {
        questionId
        order
        type
        text
        description
        required
        options {
          optionId
          text
          value
        }
        scaleConfig {
          min
          max
          minLabel
          maxLabel
        }
        matrixConfig {
          rows {
            rowId
            text
          }
          columns {
            colId
            text
            value
          }
        }
        sliderConfig {
          min
          max
          step
          minLabel
          maxLabel
          showValue
        }
        randomizeOptions
        placeholder
        territorioConfig {
          livello
          tipoRisposta
          obbligaTutteLeProvince
          provinceEscluse
        }
        media {
          type
          url
          caption
        }
        branching {
          condition {
            operator
            value
          }
          action {
            type
            targetId
          }
        }
        showIf {
          questionId
          operator
          value
          action
        }
        validation {
          minLength
          maxLength
          pattern
          minValue
          maxValue
          minSelections
          maxSelections
        }
      }
    }
    targeting {
      type
      recipientGroups {
        id
        name
      }
      manualRecipients
      manualRecipientCount
      cariche {
        id
        nome
        settore
        categoria
        livello
      }
      regioni
      province
    }
    stats {
      totalResponses
      completedResponses
      inProgressResponses
      abandonedResponses
      averageCompletionTime
      completionRate
    }
    totalQuestions
    totalSteps
    createdBy {
      id
      username
    }
    lastModifiedBy {
      id
      username
    }
    createdAt
    updatedAt
  }
`;

// ============================================
// QUERIES
// ============================================

export const GET_SURVEYS = gql`
  query GetSurveys($status: SurveyStatus, $limit: Int, $offset: Int) {
    surveys(status: $status, limit: $limit, offset: $offset) {
      ...SurveyFields
    }
  }
  ${SURVEY_FRAGMENT}
`;

export const GET_SURVEY = gql`
  query GetSurvey($id: ID, $slug: String) {
    survey(id: $id, slug: $slug) {
      ...SurveyFullFields
    }
  }
  ${SURVEY_FULL_FRAGMENT}
`;

export const GET_SURVEY_RESULTS = gql`
  query GetSurveyResults($surveyId: ID!) {
    surveyResults(surveyId: $surveyId) {
      survey {
        id
        title
        pubblico
      }
      totalResponses
      completedResponses
      completionRate
      averageTime
      questionStats {
        questionId
        questionText
        questionType
        totalAnswers
        distribution {
          value
          count
          percentage
        }
      }
      responses {
        id
        status
        respondent {
          associateId
          partitaIVA
          email
          nominativo
          codiceUnasca
          categoria
          provincia
          regione
          isAnonymous
          compilatoDa
          representativeId
          rappresentante
          carica
        }
        answers {
          questionId
          stepIndex
          value
          answeredAt
          territorio {
            livello
            regione
            provincia
          }
        }
        startedAt
        completedAt
        totalTimeSeconds
      }
    }
  }
`;

// Domanda «Territorio di competenza»: una riga per provincia delle risposte
// completate e le province dei destinatari rimaste senza risposta.
// null se il sondaggio non ha la domanda territoriale.
export const GET_SURVEY_TERRITORIO_RESULTS = gql`
  query GetSurveyTerritorioResults($surveyId: ID!) {
    surveyTerritorioResults(surveyId: $surveyId) {
      questionId
      livello
      provinceEscluse
      vociEscluse
      dettaglioEscluse {
        provincia
        provinciaNome
        regione
        voci
      }
      righe {
        regione
        provincia
        provinciaNome
        value
        etichetta
        respondent {
          associateId
          nominativo
          isAnonymous
        }
        representativeNominativo
        carica
        answeredAt
        doppia
      }
      nonCoperte {
        regione
        provincia
        provinciaNome
        apparterrebbeA {
          associateId
          nominativo
          carica
        }
      }
    }
  }
`;

// Province escluse della domanda territoriale: vale anche sui sondaggi attivi con risposte
// (le voci già raccolte restano salvate e non contano)
export const SET_SURVEY_PROVINCE_ESCLUSE = gql`
  mutation SetSurveyProvinceEscluse($surveyId: ID!, $province: [String!]!) {
    setSurveyProvinceEscluse(surveyId: $surveyId, province: $province) {
      id
    }
  }
`;

export const GET_SURVEY_NON_RESPONDENTS = gql`
  query GetSurveyNonRespondents($surveyId: ID!) {
    surveyNonRespondents(surveyId: $surveyId) {
      id
      nominativo
      partitaIVA
      email
      provincia
      regione
      stato
      categoria
    }
  }
`;

export const SEND_SURVEY_REMINDER = gql`
  mutation SendSurveyReminder($surveyId: ID!, $associateIds: [ID!], $representativeIds: [ID!]) {
    sendSurveyReminder(surveyId: $surveyId, associateIds: $associateIds, representativeIds: $representativeIds) {
      queued
      skipped
      total
    }
  }
`;

export const GET_RECIPIENT_GROUPS = gql`
  query GetRecipientGroups($type: RecipientGroupType) {
    recipientGroups(type: $type) {
      id
      name
      description
      type
      dynamicCriteria {
        stato
        categoria
        provincia
        regione
        statoTesseramento
        isCertified
        certificationStatus
        paymentStatus
        hasCertificateNumber
        gdprCompliant
        cybersecurityCompliant
        professionalInsurance
        ebasCompliant
        rappresentantiCariche
        integrativaCategoria
        hasIntegrativa
      }
      staticMembers {
        id
        nominativo
        partitaIVA
        email
        provincia
        regione
        stato
        categoria
      }
      memberCount
      lastCountUpdate
      isSystem
      createdBy {
        id
        username
      }
      createdAt
    }
  }
`;

export const GET_RECIPIENT_GROUP = gql`
  query GetRecipientGroup($id: ID!) {
    recipientGroup(id: $id) {
      id
      name
      description
      type
      dynamicCriteria {
        stato
        categoria
        provincia
        regione
        statoTesseramento
        isCertified
        certificationStatus
        paymentStatus
        hasCertificateNumber
        gdprCompliant
        cybersecurityCompliant
        professionalInsurance
        ebasCompliant
        iscrizioneDa
        iscrizioneA
        rappresentantiCariche
        integrativaCategoria
        hasIntegrativa
      }
      memberCount
      lastCountUpdate
      isSystem
      createdBy {
        id
        username
      }
      createdAt
      updatedAt
    }
  }
`;

export const PREVIEW_RECIPIENTS = gql`
  query PreviewRecipients($criteria: DynamicCriteriaInput!, $limit: Int, $offset: Int) {
    previewRecipients(criteria: $criteria, limit: $limit, offset: $offset) {
      total
      sample {
        id
        nominativo
        partitaIVA
        email
        provincia
        regione
        stato
        categoria
      }
    }
  }
`;

export const GET_PROVINCE_SUMMARY = gql`
  query GetProvinceSummary {
    provinceSummary {
      provincia
      regione
      count
    }
  }
`;

export const SEARCH_ASSOCIATES = gql`
  query SearchAssociates($query: String!, $limit: Int) {
    searchAssociates(query: $query, limit: $limit) {
      id
      nominativo
      partitaIVA
      email
      provincia
      regione
      stato
      categoria
    }
  }
`;

export const GET_ASSOCIATIVE_ROLES_FOR_GROUPS = gql`
  query GetAssociativeRolesForGroups {
    associativeRolesForGroups {
      id
      nome
      categoria
      settore
      livello
    }
  }
`;

// ============================================
// MUTATIONS
// ============================================

export const CREATE_SURVEY = gql`
  mutation CreateSurvey($input: CreateSurveyInput!) {
    createSurvey(input: $input) {
      ...SurveyFullFields
    }
  }
  ${SURVEY_FULL_FRAGMENT}
`;

export const UPDATE_SURVEY = gql`
  mutation UpdateSurvey($id: ID!, $input: UpdateSurveyInput!) {
    updateSurvey(id: $id, input: $input) {
      ...SurveyFullFields
    }
  }
  ${SURVEY_FULL_FRAGMENT}
`;

export const DELETE_SURVEY = gql`
  mutation DeleteSurvey($id: ID!) {
    deleteSurvey(id: $id)
  }
`;

export const DUPLICATE_SURVEY = gql`
  mutation DuplicateSurvey($id: ID!, $newTitle: String!) {
    duplicateSurvey(id: $id, newTitle: $newTitle) {
      ...SurveyFields
    }
  }
  ${SURVEY_FRAGMENT}
`;

export const PUBLISH_SURVEY = gql`
  mutation PublishSurvey($id: ID!) {
    publishSurvey(id: $id) {
      id
      status
      startDate
    }
  }
`;

export const PAUSE_SURVEY = gql`
  mutation PauseSurvey($id: ID!) {
    pauseSurvey(id: $id) {
      id
      status
    }
  }
`;

export const CLOSE_SURVEY = gql`
  mutation CloseSurvey($id: ID!) {
    closeSurvey(id: $id) {
      id
      status
      endDate
    }
  }
`;

// Recipient Groups Mutations
export const CREATE_RECIPIENT_GROUP = gql`
  mutation CreateRecipientGroup($input: CreateRecipientGroupInput!) {
    createRecipientGroup(input: $input) {
      id
      name
      description
      type
      memberCount
    }
  }
`;

export const UPDATE_RECIPIENT_GROUP = gql`
  mutation UpdateRecipientGroup($id: ID!, $input: UpdateRecipientGroupInput!) {
    updateRecipientGroup(id: $id, input: $input) {
      id
      name
      description
      memberCount
    }
  }
`;

export const DELETE_RECIPIENT_GROUP = gql`
  mutation DeleteRecipientGroup($id: ID!) {
    deleteRecipientGroup(id: $id)
  }
`;

export const REFRESH_RECIPIENT_GROUP_COUNT = gql`
  mutation RefreshRecipientGroupCount($id: ID!) {
    refreshRecipientGroupCount(id: $id) {
      id
      memberCount
      lastCountUpdate
    }
  }
`;

// ============================================
// ASSOCIATI - COMPILAZIONE SONDAGGI
// ============================================

export const GET_AVAILABLE_SURVEYS = gql`
  query GetAvailableSurveys($partitaIVA: String!) {
    availableSurveys(partitaIVA: $partitaIVA) {
      id
      title
      description
      status
      startDate
      endDate
      avvisoTerritorio
      settings {
        showProgressBar
      }
      steps {
        stepId
        title
      }
    }
  }
`;

// Dipendente dello studio: come GET_AVAILABLE_SURVEYS più `rispondeSoloTitolare` (true se il
// titolare non ha aperto i sondaggi ai dipendenti). Si usa SOLO per il dipendente, con il suo Bearer
// e il client dei sondaggi: i dipendenti esistono solo con un backend che conosce il campo, il
// titolare resta sulla query di sempre.
export const GET_AVAILABLE_SURVEYS_DIPENDENTE = gql`
  query GetAvailableSurveysDipendente($partitaIVA: String!) {
    availableSurveys(partitaIVA: $partitaIVA) {
      id
      title
      description
      status
      startDate
      endDate
      avvisoTerritorio
      rispondeSoloTitolare
      settings {
        showProgressBar
      }
      steps {
        stepId
        title
      }
    }
  }
`;

export const PREVIEW_SURVEY_RECIPIENTS = gql`
  query PreviewSurveyRecipients($targeting: SurveyTargetingInput!, $livelloTerritorio: String, $pubblico: SurveyPubblico) {
    previewSurveyRecipients(targeting: $targeting, livelloTerritorio: $livelloTerritorio, pubblico: $pubblico) {
      total
      withEmail
      withoutEmail
      sample {
        id
        nominativo
        partitaIVA
        email
        provincia
        regione
        categoria
        emailOrigine
        rappresentante
      }
    }
  }
`;

export const VERIFY_SURVEY_MAGIC_LINK = gql`
  query VerifySurveyMagicLink($surveyId: ID!, $token: String!) {
    verifySurveyMagicLink(surveyId: $surveyId, token: $token) {
      valid
      partitaIVA
      error
      identitaVerificata
      linkFirmato
    }
  }
`;

export const GET_SURVEY_FOR_RESPONSE = gql`
  query GetSurveyForResponse($id: ID!) {
    survey(id: $id) {
      id
      title
      description
      status
      startDate
      endDate
      identitaVerificata
      settings {
        showProgressBar
        requireAllQuestions
        randomizeQuestions
        thankYouMessage
        redirectUrl
        welcomeMessage
        closedMessage
        allowSaveDraft
        timeLimitMinutes
        maxResponses
      }
      steps {
        stepId
        order
        title
        description
        media {
          type
          url
          filename
          caption
        }
        questions {
          questionId
          order
          type
          text
          description
          required
          options {
            optionId
            text
            value
          }
          scaleConfig {
            min
            max
            minLabel
            maxLabel
          }
          matrixConfig {
            rows {
              rowId
              text
            }
            columns {
              colId
              text
              value
            }
          }
          sliderConfig {
            min
            max
            step
            minLabel
            maxLabel
            showValue
          }
          placeholder
          territorioConfig {
            livello
            tipoRisposta
            obbligaTutteLeProvince
            provinceEscluse
          }
          media {
            type
            url
            filename
            caption
          }
          branching {
            condition {
              operator
              value
            }
            action {
              type
              targetId
            }
          }
          showIf {
            questionId
            operator
            value
            action
          }
          validation {
            minLength
            maxLength
            minValue
            maxValue
            minSelections
            maxSelections
            pattern
          }
        }
      }
    }
  }
`;

// Sondaggi con identità verificata: identità dal Bearer associato (header nel
// context dell'operazione, vedi services/surveyAuth.ts) o da `linkToken`.
// Campi della risposta restituiti da TUTTE le operazioni di risposta: stessa
// selezione ovunque, così la cache non resta con voci incomplete (senza
// territorio) e la query della risposta non riparte dal server.
const RISPOSTA_SONDAGGIO_FRAGMENT = gql`
  fragment RispostaSondaggio on SurveyResponse {
    id
    status
    currentStepIndex
    startedAt
    completedAt
    respondent {
      territorio {
        livello
        regioni
        province
      }
    }
    answers {
      questionId
      stepIndex
      value
      answeredAt
      territorio {
        livello
        regione
        provincia
      }
    }
  }
`;

export const GET_MY_SURVEY_RESPONSE = gql`
  query GetMySurveyResponse($surveyId: ID!, $partitaIVA: String, $linkToken: String) {
    mySurveyResponse(surveyId: $surveyId, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

// Chi ha compilato la risposta dello studio (titolare o dipendente). Query a parte e non nel
// frammento comune: con un backend che non conosce `compilatoDa` fallisce solo questa (errore
// ignorato dalla pagina) e le operazioni di risposta restano quelle di sempre.
export const GET_COMPILATO_DA_SONDAGGIO = gql`
  query GetCompilatoDaSondaggio($surveyId: ID!, $partitaIVA: String, $linkToken: String) {
    mySurveyResponse(surveyId: $surveyId, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      id
      compilatoDa {
        tipo
        dipendenteId
        nome
      }
    }
  }
`;

// Risposte inviate (sola lettura): la risposta con le domande del sondaggio.
// Il sondaggio viene dalla risposta e non da `survey(id)`, che ai non admin
// restituisce solo i sondaggi attivi: così le risposte restano consultabili
// anche a sondaggio chiuso.
export const GET_MY_SURVEY_ANSWERS = gql`
  query GetMySurveyAnswers($surveyId: ID!, $partitaIVA: String, $linkToken: String) {
    mySurveyResponse(surveyId: $surveyId, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
      totalTimeSeconds
      respondent {
        nominativo
        partitaIVA
        rappresentante
        carica
      }
      survey {
        id
        title
        description
        startDate
        endDate
        settings {
          welcomeMessage
        }
        steps {
          stepId
          order
          title
          description
          questions {
            questionId
            order
            type
            text
            description
            options {
              optionId
              text
              value
            }
            matrixConfig {
              rows {
                rowId
                text
              }
              columns {
                colId
                text
                value
              }
            }
            territorioConfig {
              livello
            }
            showIf {
              questionId
              operator
              value
              action
            }
          }
        }
      }
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

export const START_SURVEY_RESPONSE = gql`
  mutation StartSurveyResponse($surveyId: ID!, $partitaIVA: String, $linkToken: String) {
    startSurveyResponse(surveyId: $surveyId, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

export const SUBMIT_SURVEY_ANSWER = gql`
  mutation SubmitSurveyAnswer($responseId: ID!, $questionId: String!, $value: String!, $partitaIVA: String, $linkToken: String) {
    submitSurveyAnswer(responseId: $responseId, questionId: $questionId, value: $value, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

// Domanda «Territorio di competenza»: TUTTE le province compilate in un colpo
// (il server sostituisce in blocco le voci della domanda).
export const SUBMIT_SURVEY_TERRITORIO_ANSWERS = gql`
  mutation SubmitSurveyTerritorioAnswers($responseId: ID!, $questionId: String!, $risposte: [RispostaTerritorioInput!]!, $linkToken: String) {
    submitSurveyTerritorioAnswers(responseId: $responseId, questionId: $questionId, risposte: $risposte, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

export const SAVE_SURVEY_PROGRESS = gql`
  mutation SaveSurveyProgress($responseId: ID!, $currentStepIndex: Int!, $partitaIVA: String, $linkToken: String) {
    saveSurveyProgress(responseId: $responseId, currentStepIndex: $currentStepIndex, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

export const COMPLETE_SURVEY_RESPONSE = gql`
  mutation CompleteSurveyResponse($responseId: ID!, $partitaIVA: String, $linkToken: String) {
    completeSurveyResponse(responseId: $responseId, partitaIVA: $partitaIVA, linkToken: $linkToken) {
      ...RispostaSondaggio
    }
  }
  ${RISPOSTA_SONDAGGIO_FRAGMENT}
`;

export const GET_MY_COMPLETED_SURVEYS = gql`
  query GetMyCompletedSurveys($partitaIVA: String!) {
    myCompletedSurveys(partitaIVA: $partitaIVA) {
      surveyId
      title
      description
      completedAt
      stepCount
    }
  }
`;

// Sondaggi chiusi o scaduti senza una risposta inviata (destinatari secondo i criteri attuali), sola lettura
export const GET_MY_EXPIRED_SURVEYS = gql`
  query GetMyExpiredSurveys($partitaIVA: String!) {
    myExpiredSurveys(partitaIVA: $partitaIVA) {
      surveyId
      title
      description
      closedAt
      stepCount
    }
  }
`;
